import { useQuery } from '@tanstack/react-query';
import { api } from 'src/common/utils/api';
import type { MeetingScope } from 'src/meetings/scope';
import type { CalendarEvent } from 'src/meetings/models';
import { useCalendarEvents } from 'src/meetings/hooks/useCalendarEvents';

export type FamilyMeeting = CalendarEvent;

type ScopedMeetingsOptions = {
  // Only used by the practice-wide ('all') scope, which is paginated per section.
  section?: 'upcoming' | 'past';
  clientMeetingsOnly?: boolean;
};

function meetingsResource(scope: MeetingScope): string {
  switch (scope.kind) {
    case 'family':
      return `families/${scope.familyId}/calendar-events`;
    case 'individual':
      return `clients/${scope.clientId}/calendar-events`;
    case 'all':
      return 'calendar-events';
  }
}

export function meetingsQueryKey(scope: MeetingScope): readonly unknown[] {
  switch (scope.kind) {
    case 'family':
      return ['families', scope.familyId, 'calendar-events'];
    case 'individual':
      return ['clients', scope.clientId, 'calendar-events'];
    case 'all':
      return ['calendar-events'];
  }
}

const noop = () => {};

export const useScopedMeetings = (scope: MeetingScope, options: ScopedMeetingsOptions = {}) => {
  const isAll = scope.kind === 'all';

  // A client or family has a bounded set of meetings, so a single large page is
  // enough. The practice-wide hub can have far more, so it fetches per section
  // with pagination via useCalendarEvents below.
  const scopedQuery = useQuery({
    queryKey: meetingsQueryKey(scope),
    queryFn: () => api.getPaginated<FamilyMeeting>(meetingsResource(scope), { page: 1, size: 100 }),
    enabled: !isAll,
  });

  const globalQuery = useCalendarEvents({
    pastOrUpcoming: options.section === 'past' ? 'PAST' : 'UPCOMING',
    clientMeetingsOnly: options.clientMeetingsOnly,
    enabled: isAll,
  });

  if (isAll) {
    return {
      events: globalQuery.events,
      isLoading: globalQuery.isLoading,
      error: globalQuery.error,
      hasNextPage: globalQuery.hasNextPage,
      fetchNextPage: globalQuery.fetchNextPage,
      isFetchingNextPage: globalQuery.isFetchingNextPage,
      refetch: globalQuery.refetch,
    };
  }

  return {
    events: scopedQuery.data?.items ?? [],
    isLoading: scopedQuery.isLoading,
    error: scopedQuery.error,
    hasNextPage: false,
    fetchNextPage: noop,
    isFetchingNextPage: false,
    refetch: scopedQuery.refetch,
  };
};
